import React from 'react';
import Markdown from 'markdown-to-jsx';
import * as S from './markdown-wrapper.styles';

// Normalize fenced code blocks in Markdown so renderers handle them consistently
export const normalizeMarkdown = (text: string): string => {
    if (!text) return text;

    const CODE_BLOCK_PLACEHOLDER = '___CODE_BLOCK_PLACEHOLDER___';
    const codeBlocks: string[] = [];

    // helpers
    const looksLikeTable = (content: string): boolean => {
        const lines = content
            .trim()
            .split('\n')
            .map(l => l.trim());
        if (lines.length < 2) return false;

        // require at least 2 rows with pipes
        const firstHasPipes = lines[0].includes('|');
        const secondHasPipes = lines[1].includes('|');

        return firstHasPipes && secondHasPipes;
    };

    const normalizeTable = (content: string): string => {
        const lines = content
            .trim()
            .split('\n')
            .map(l => l.trim())
            .filter(Boolean);

        if (lines.length < 2) return content;

        // safer column count
        const cells = lines[0].split('|').filter(c => c.trim() !== '');
        const colCount = cells.length || 1;

        // Ensure separator row is valid
        if (!/^(\|?\s*:?-+:?\s*\|)+\s*$/.test(lines[1])) {
            const separator = '| ' + Array(colCount).fill('---').join(' | ') + ' |';
            lines.splice(1, 0, separator);
        }

        return lines.join('\n');
    };

    // extract code blocks
    let normalized = text.replace(/```([\s\S]*?)```/g, match => {
        const index = codeBlocks.length;
        codeBlocks.push(match);
        return `${CODE_BLOCK_PLACEHOLDER}${index}${CODE_BLOCK_PLACEHOLDER}`;
    });

    // escape CONSTANT_CASE underscores
    normalized = normalized.replace(/\b[A-Z0-9]+(_[A-Z0-9]+)+\b/g, match => match.replace(/_/g, '\\_'));

    // restore code blocks
    normalized = normalized.replace(
        new RegExp(`${CODE_BLOCK_PLACEHOLDER}(\\d+)${CODE_BLOCK_PLACEHOLDER}`, 'g'),
        (_, index: string) => {
            const codeBlock = codeBlocks[parseInt(index, 10)];
            const inner = codeBlock.slice(3, -3).trim();

            if (!inner) return codeBlock;

            // detect language
            const langMatch = inner.match(/^([\w-]+)\n([\s\S]*)$/);

            // unwrap markdown tables ONLY if no language
            if (!langMatch && looksLikeTable(inner)) {
                return `\n\n${normalizeTable(inner)}\n\n`;
            }

            if (langMatch) {
                const [, lang, content] = langMatch;
                if (!content.trim()) return codeBlock;

                return `\n\n\`\`\`${lang}\n${content.trim()}\n\`\`\`\n\n`;
            }

            return `\n\n\`\`\`\n${inner}\n\`\`\`\n\n`;
        },
    );

    return normalized;
};

const BlockedComponent: React.FunctionComponent = (): React.ReactElement => {
    return <p>This component is blocked for security reasons</p>;
};

type MarkdownWrapperProps = {
    className?: string;
    text?: string;
    children?: string;
};

const MarkdownWrapper: React.FunctionComponent<MarkdownWrapperProps> = ({ className, text, children }) => {
    const content = text ?? children ?? '';

    return (
        <Markdown
            className={className}
            options={{
                overrides: {
                    iframe: {
                        component: BlockedComponent,
                    },
                    table: S.MarkdownTable,
                    th: S.MarkdownTableTh,
                    td: S.MarkdownTableTd,
                },
            }}>
            {normalizeMarkdown(content)}
        </Markdown>
    );
};

export default MarkdownWrapper;
